import { createContext, useContext } from "react";

export const PreviewTenantContext = createContext<string | undefined>(undefined);
export function usePreviewTenantId() { return useContext(PreviewTenantContext); }

/** The parts of a session this rule needs. */
interface EndOrgSource {
  orgId?: string;
  endOrgId?: string;
}

/**
 * Which customer org the hub is acting for.
 *
 * The rule was `previewTenantId ?? userContext.orgId`, which never consulted
 * `endOrgId` — so inside the embed, where `/embed/token/validate` answers
 * `orgId = the PARTNER` and `endOrgId = the customer the token names`, the whole
 * hub retargeted at the partner: it listed the partner's own credentials, the
 * installation wizard pre-pinned one of them, and the customer came out
 * "Connected" against the partner's account.
 *
 * `endOrgId` is set exactly when the session is already acting for a tenant —
 * an embed token, an `end_org_id` JWT claim, a switched account, or
 * impersonation — so preferring it is right on every surface, and a plain
 * dashboard session (no `endOrgId`) still resolves to its own org.
 *
 * An explicit preview selection still wins. The empty string is treated as
 * absent because an org-level embed token stores `endOrgId: ""`, which `??`
 * would otherwise pass through as a real org id.
 */
export function resolveEndOrgId(
  previewTenantId: string | undefined,
  session: EndOrgSource | null | undefined,
): string | undefined {
  return previewTenantId || session?.endOrgId || session?.orgId || undefined;
}
